<template>
  <el-dialog
    v-model="visible"
    :title="$t('ucJoinChannel.title')"
    width="95%"
    :close-on-click-modal="false"
    class="custom-channel-dialog"
    top="5vh"
    @open="handleOpen"
  >
    <div
      v-loading="dialogLoading"
      class="channel-dialog-container"
    >
      <!-- 左侧主渠道类型 -->
      <div class="left-tabs">
        <div
          v-for="item in channelTypes"
          :key="item.value"
          class="left-tab-item"
          :class="{ active: activeChannelType === item.value }"
          @click="handleChannelTypeChange(item.value)"
        >
          {{ item.label }}
        </div>
      </div>
      
      <!-- 右侧内容 -->
      <div class="right-content">
        <!-- 一级渠道 -->
        <div
          v-if="primaryChannelTabs.length > 0"
          class="primary-tabs"
        >
          <div
            v-for="tab in primaryChannelTabs"
            :key="tab.id"
            class="primary-tab-item"
            :class="{ active: activePrimaryChannelId === tab.id }"
            :title="tab.label"
            @click="handlePrimaryChannelChange(tab.id)"
          >
            {{ tab.label }}
          </div>
        </div>

        <!-- 渠道身份类型（专项治理须先有一级渠道） -->
        <div
          v-if="showSubChannelPanels"
          class="type-tabs-wrapper"
        >
          <div class="type-tabs">
            <button
              v-for="tab in currentSubTabs"
              :key="tab.value"
              type="button"
              class="type-tab-item"
              :class="{ active: activeSubTab === tab.value }"
              @click="handleSubTabChange(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <!-- 区级协会区域类型 -->
        <div
          v-if="showSubChannelPanels && showDistrictTabs"
          class="district-tabs"
        >
          <div
            v-for="tab in channelDistrictTypeOptions"
            :key="tab.value"
            class="district-tab-item"
            :class="{ active: activeDistrictType === tab.value }"
            @click="handleDistrictTypeChange(tab.value)"
          >
            {{ tab.label }}
          </div>
        </div>
        
        <!-- 渠道网格 -->
        <div class="channel-grid-wrapper">
          <div
            v-if="showSubChannelPanels && displayedChannels.length > 0"
            class="channel-grid"
          >
            <div
              v-for="channel in displayedChannels"
              :key="channel.id"
              class="channel-box"
              :class="{ active: selectedChannelId === channel.id }"
              @click="selectedChannelId = channel.id"
            >
              <span
                class="channel-leading"
                aria-hidden="true"
              >
                <span
                  v-if="selectedChannelId === channel.id"
                  class="active-icon"
                >
                  <el-icon><Check /></el-icon>
                </span>
              </span>
              <span
                class="channel-name"
                :title="channel.channelOrgName"
              >{{ channel.channelOrgName }}</span>
              <span
                class="channel-trailing"
                aria-hidden="true"
              />
            </div>
          </div>
          <EmptyPlaceholder
            v-else
            fill
          />
        </div>
        
        <!-- 底部操作区 -->
        <div class="bottom-action">
          <span class="required-star">*</span>
          <span class="label-text">{{ $t('ucJoinChannel.form.channelCode') }}</span>
          <el-input
            v-model="channelCode"
            :placeholder="$t('ucJoinChannel.placeholder.channelCode')"
            class="code-input"
            :disabled="dialogLoading"
            @keyup.enter="handleSubmitChannel"
          />
          <el-button
            type="primary"
            class="join-btn"
            :loading="submitLoading"
            :disabled="dialogLoading"
            @click="handleSubmitChannel"
          >
            {{ $t('ucJoinChannel.btn.join') }}
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Check } from '@element-plus/icons-vue'
import {
  getChannelEnterpriseList,
  getPrimaryChannelEnterpriseList,
  joinChannelEnterprise
} from '@/api/userCenter'
import {
  getChannelDistrictTypeList,
  getGovernanceChannelIdentityTypeList,
  getIndustryChannelIdentityTypeList
} from '@/api/dict'
import {
  ChannelTypeEnum,
  GOVERNANCE_CHANNEL_IDENTITY_DISTRICT_ASSOC,
  GOVERNANCE_CHANNEL_IDENTITY_PRIMARY
} from '@/enums/ChannelTypeEnum'
import EmptyPlaceholder from '@/components/EmptyPlaceholder.vue'

/** 数据字典固定结构：{ code: 200, data: [{ dictLabel, dictValue, dictSort, ... }] } */
function mapDictOptions(res) {
  if (!res || res.code !== 200 || !Array.isArray(res.data)) {
    return []
  }
  return res.data
    .slice()
    .sort((a, b) => (a.dictSort ?? 0) - (b.dictSort ?? 0))
    .map(item => ({
      label: item.dictLabel,
      value: String(item.dictValue)
    }))
}

/** 列表接口固定结构：{ code: 200, data: [] } */
function mapDataList(res) {
  if (!res || res.code !== 200 || !Array.isArray(res.data)) {
    return []
  }
  return res.data
}

export default {
  name: 'JoinChannelDialog',
  components: {
    Check,
    EmptyPlaceholder
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      channelTypes: [
        ChannelTypeEnum.GOVERNANCE_CHANNEL,
        ChannelTypeEnum.INDUSTRY_CHANNEL
      ],
      activeChannelType: ChannelTypeEnum.GOVERNANCE_CHANNEL.value,
      activePrimaryChannelId: '',
      activeSubTab: '',
      activeDistrictType: '',
      govSubTabs: [],
      indSubTabs: [],
      channelDistrictTypeOptions: [],
      primaryChannels: [],
      allChannels: [],
      selectedChannelId: '',
      channelCode: '',
      dialogLoading: false,
      submitLoading: false
    }
  },
  computed: {
    visible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    currentSubTabs() {
      return this.activeChannelType === ChannelTypeEnum.GOVERNANCE_CHANNEL.value ? this.govSubTabs : this.indSubTabs
    },
    primaryChannelTabs() {
      if (this.activeChannelType !== ChannelTypeEnum.GOVERNANCE_CHANNEL.value) return []
      return this.primaryChannels
        .filter(c => c.id !== null && c.id !== undefined)
        .map(c => ({
          id: String(c.id),
          label: c.channelOrgName || this.$t('ucJoinChannel.fallback.unnamedChannel')
        }))
    },
    /** 专项治理工程渠道须有一级渠道，才展示下级渠道类型/区域/网格；版权产业渠道不受此限 */
    showSubChannelPanels() {
      if (this.activeChannelType === ChannelTypeEnum.INDUSTRY_CHANNEL.value) {
        return true
      }
      return this.primaryChannelTabs.length > 0
    },
    hasChannelParentRelation() {
      return this.allChannels.some(c => String(c.channelParentId ?? '') !== '')
    },
    isDistrictAssocSelected() {
      return this.activeChannelType === ChannelTypeEnum.GOVERNANCE_CHANNEL.value &&
        String(this.activeSubTab) === GOVERNANCE_CHANNEL_IDENTITY_DISTRICT_ASSOC
    },
    showDistrictTabs() {
      return this.isDistrictAssocSelected && this.channelDistrictTypeOptions.length > 0
    },
    displayedChannels() {
      if (!this.showSubChannelPanels) {
        return []
      }
      return this.allChannels.filter(c => {
        const matchedBase =
          String(c.channelType) === this.activeChannelType &&
          String(c.channelIdentityType) === this.activeSubTab
        if (!matchedBase) return false
        if (this.activePrimaryChannelId && this.hasChannelParentRelation) {
          if (String(c.channelParentId ?? '') !== this.activePrimaryChannelId) return false
        }
        if (this.showDistrictTabs && this.activeDistrictType) {
          return String(c.channelDistrictType ?? '') === this.activeDistrictType
        }
        return true
      })
    }
  },
  methods: {
    async handleOpen() {
      this.selectedChannelId = ''
      this.channelCode = ''
      this.submitLoading = false
      this.activeChannelType = ChannelTypeEnum.GOVERNANCE_CHANNEL.value
      this.activePrimaryChannelId = ''
      this.activeSubTab = ''
      this.activeDistrictType = ''
      this.primaryChannels = []
      this.allChannels = []
      this.dialogLoading = true

      try {
        const governanceSubTabsPromise = this.govSubTabs.length === 0
          ? getGovernanceChannelIdentityTypeList().then(res => {
            this.govSubTabs = mapDictOptions(res).filter(
              item => item.value !== GOVERNANCE_CHANNEL_IDENTITY_PRIMARY
            )
          })
          : Promise.resolve()

        const industrySubTabsPromise = this.indSubTabs.length === 0
          ? getIndustryChannelIdentityTypeList().then(res => {
            this.indSubTabs = mapDictOptions(res)
          })
          : Promise.resolve()

        const districtTypeOptionsPromise = this.channelDistrictTypeOptions.length === 0
          ? getChannelDistrictTypeList().then(res => {
            this.channelDistrictTypeOptions = mapDictOptions(res)
          })
          : Promise.resolve()

        const primaryChannelsPromise = getPrimaryChannelEnterpriseList().then(res => {
          this.primaryChannels = mapDataList(res)
        })

        const channelListPromise = getChannelEnterpriseList().then(res => {
          this.allChannels = mapDataList(res)
        })

        await Promise.all([
          governanceSubTabsPromise,
          industrySubTabsPromise,
          districtTypeOptionsPromise,
          primaryChannelsPromise,
          channelListPromise
        ])

        this.setDefaultPrimaryChannel()
        this.setDefaultSubTab()
      } catch (error) {
        console.error('加载渠道数据失败:', error)
      } finally {
        this.dialogLoading = false
      }
    },
    handleChannelTypeChange(val) {
      if (this.dialogLoading) return
      this.activeChannelType = val
      this.setDefaultPrimaryChannel()
      this.setDefaultSubTab()
      this.selectedChannelId = ''
    },
    handlePrimaryChannelChange(val) {
      if (this.dialogLoading) return
      this.activePrimaryChannelId = val
      this.selectedChannelId = ''
    },
    handleSubTabChange(val) {
      if (this.dialogLoading) return
      this.activeSubTab = val
      this.setDefaultDistrictType()
      this.selectedChannelId = ''
    },
    handleDistrictTypeChange(val) {
      if (this.dialogLoading) return
      this.activeDistrictType = val
      this.selectedChannelId = ''
    },
    setDefaultPrimaryChannel() {
      const tabs = this.primaryChannelTabs
      const currentStillExists = tabs.some(t => t.id === this.activePrimaryChannelId)
      this.activePrimaryChannelId = currentStillExists ? this.activePrimaryChannelId : (tabs[0]?.id || '')
    },
    setDefaultSubTab() {
      const tabs = this.currentSubTabs
      const currentStillExists = tabs.some(t => t.value === this.activeSubTab)
      this.activeSubTab = currentStillExists ? this.activeSubTab : (tabs[0]?.value || '')
      this.setDefaultDistrictType()
    },
    setDefaultDistrictType() {
      if (!this.isDistrictAssocSelected) {
        this.activeDistrictType = ''
        return
      }
      const tabs = this.channelDistrictTypeOptions
      const currentStillExists = tabs.some(t => t.value === this.activeDistrictType)
      this.activeDistrictType = currentStillExists ? this.activeDistrictType : (tabs[0]?.value || '')
    },
    async handleSubmitChannel() {
      if (this.dialogLoading || this.submitLoading) {
        return
      }

      if (this.selectedChannelId === '') {
        ElMessage.warning(this.$t('ucJoinChannel.msg.selectChannel'))
        return
      }

      if (!this.channelCode || this.channelCode.trim() === '') {
        ElMessage.warning(this.$t('ucJoinChannel.msg.inputChannelCode'))
        return
      }

      this.submitLoading = true
      try {
        const response = await joinChannelEnterprise(
          Number(this.selectedChannelId),
          this.channelCode.trim()
        )

        if (response && response.code === 200) {
          ElMessage.success(this.$t('ucJoinChannel.msg.joinSuccess'))
          this.visible = false
          this.$emit('success')
        }
      } catch (error) {
        console.error('提交渠道认证失败:', error)
      } finally {
        this.submitLoading = false
      }
    }
  }
}
</script>

<style lang="scss">
.custom-channel-dialog {
  max-width: 1440px;

  .el-dialog__body {
    padding: 0 !important;
  }
  .el-dialog__header {
    margin-right: 0;
    border-bottom: 1px solid #f0f0f0;
    padding: 20px 24px;
    margin-bottom: 0;
    
    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }
}

@media (max-width: 768px) {
  .custom-channel-dialog {
    width: calc(100vw - 16px) !important;
    max-width: none;
    margin-top: 8px !important;
    border-radius: 8px;
    overflow: hidden;

    .el-dialog__header {
      padding: 14px 18px;

      .el-dialog__title {
        font-size: 16px;
      }
    }
  }
}
</style>

<style lang="scss" scoped>

@mixin hover-scrollbar($size: 4px, $reserve-space: false) {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  @if $reserve-space {
    padding-bottom: $size;
    box-sizing: border-box;
  }

  &::-webkit-scrollbar {
    width: $size;
    height: $size;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 999px;
    transition: background-color 0.2s;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &:hover {
    scrollbar-color: #c0c4cc transparent;

    &::-webkit-scrollbar-thumb {
      background-color: #c0c4cc;
    }
  }
}

.channel-dialog-container {
  display: flex;
  height: min(660px, calc(100vh - 132px));
  min-height: 460px;
  background: #fff;

  @media (max-width: 1024px) {
    flex-direction: column;
    height: calc(100vh - 112px);
    min-height: 420px;
    max-height: 720px;
    overflow: hidden;
  }

  @media (max-width: 768px) {
    height: calc(100dvh - 82px);
    min-height: 0;
    max-height: none;
  }
}

.left-tabs {
  width: 160px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 8px;
  background: #f5f7fa;
  border-right: 1px solid #e4e7ed;

  @media (max-width: 1024px) {
    width: 100%;
    flex-direction: row;
    gap: 8px;
    padding: 10px 12px;
    border-right: none;
    border-bottom: 1px solid #e4e7ed;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 768px) {
    padding: 8px;
    gap: 6px;
  }
}

.left-tab-item {
  padding: 12px 8px;
  font-size: 16px;
  font-weight: 500;
  color: #191919;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  margin-bottom: 4px;
  border-radius: 6px;
  line-height: 1.5;
  white-space: nowrap;

  @media (max-width: 1024px) {
    flex: 0 0 auto;
    min-width: 148px;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    min-width: 124px;
    padding: 10px 12px;
    font-size: 14px;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background: #ecf5ff;
    color: #0d62f3;
  }
  
  &.active {
    background: #0d62f3;
    color: #fff;
  }
}

.right-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.primary-tabs {
  display: flex;
  min-height: 64px;
  padding: 0 20px;
  border-bottom: 1px solid #f0f0f0;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  min-width: 0;
  background: #fff;
  @include hover-scrollbar(4px, true);

  @media (max-width: 1024px) {
    min-height: 56px;
    padding: 0 14px;
  }

  @media (max-width: 768px) {
    min-height: 48px;
    padding: 0 8px;
  }
}

.primary-tab-item {
  flex: 0 0 auto;
  min-width: clamp(136px, 18vw, 220px);
  max-width: 260px;
  min-height: 64px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-size: 15px;
  font-weight: 500;
  color: #191919;
  cursor: pointer;
  position: relative;
  text-align: center;
  line-height: 1.35;
  transition: color 0.2s;

  @media (max-width: 1024px) {
    min-height: 56px;
    min-width: 148px;
    max-width: 220px;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    min-width: 118px;
    max-width: 168px;
    min-height: 48px;
    padding: 0 8px;
    font-size: 13px;
  }
  
  &:hover,
  &.active {
    color: #0D62F3;
    font-weight: 600;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 12px;
      right: 12px;
      height: 3px;
      background-color: #0D62F3;
    }
  }
}

.type-tabs-wrapper {
  padding: 12px 20px 10px;
  flex-shrink: 0;
  background: #fff;

  @media (max-width: 1024px) {
    padding: 10px 14px 8px;
    overflow-x: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 768px) {
    padding: 8px;
  }
}

.type-tabs {
  display: inline-flex;
  align-items: center;
  width: max-content;
  min-width: max-content;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: visible;
  background: #fff;

  &::-webkit-scrollbar {
    display: none;
  }
}

.type-tab-item {
  flex: 0 0 auto;
  min-width: 104px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-right: 1px solid #e8e8e8;
  background: #fff;
  color: #606266;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:last-child {
    border-right: none;
  }

  &:hover {
    color: #0d62f3;
  }

  &.active {
    background: #0d62f3;
    color: #fff;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    min-width: 82px;
    height: 32px;
    padding: 0 10px;
    font-size: 13px;
  }
}

.district-tabs {
  display: flex;
  flex-wrap: nowrap;
  min-height: 42px;
  margin: 0 20px;
  border-bottom: 1px solid #f0f0f0;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  min-width: 0;
  @include hover-scrollbar(4px, true);

  @media (max-width: 1024px) {
    margin: 0 14px;
  }

  @media (max-width: 768px) {
    min-height: 36px;
    margin: 0 8px;
  }
}

.district-tab-item {
  flex: 0 0 auto;
  min-width: 112px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #191919;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  transition: color 0.2s;

  &:hover,
  &.active {
    color: #0d62f3;
    font-weight: 600;

    &::after {
      content: '';
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: 0;
      height: 3px;
      background: #0d62f3;
    }
  }

  @media (max-width: 768px) {
    min-width: 88px;
    padding: 0 8px;
    font-size: 13px;
  }
}

.channel-grid-wrapper {
  flex: 1;
  min-height: 0;
  min-width: 0;
  padding: 14px 20px 24px;
  overflow-x: auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  @include hover-scrollbar(6px);

  @media (max-width: 1024px) {
    padding: 12px 14px 18px;
  }

  @media (max-width: 768px) {
    padding: 10px 8px 12px;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
}

.channel-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px 20px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
}

.channel-box {
  width: 100%;
  min-width: 0;
  height: 40px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #E8E8E8;
  // 左勾选占位 + 中间文案 + 右侧等宽占位，选中/未选中文案区域宽度一致，省略号行为一致
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 24px;
  align-items: center;
  padding: 0 8px;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.3s, background-color 0.3s;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 42px;
    padding: 0 10px;
    grid-template-columns: 20px minmax(0, 1fr) 20px;
  }
  
  &:hover {
    border-color: #0D62F3;
  }
  
  &.active {
    border-color: #0D62F3;
    background-color: #f0f5ff;
    
    .channel-name {
      color: #0D62F3;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  
  .channel-leading {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 24px;
    min-width: 24px;
  }
  
  .channel-trailing {
    width: 24px;
    min-width: 24px;
    justify-self: end;
  }
  
  .active-icon {
    width: 14px;
    height: 14px;
    background-color: #0D62F3;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    .el-icon {
      color: #fff;
      font-size: 10px;
      font-weight: bold;
    }
  }
  
  .channel-name {
    min-width: 0;
    max-width: 100%;
    font-size: 14px;
    font-weight: 400;
    color: #191919;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
    padding: 0;
    display: block;
  }
}

.bottom-action {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex-shrink: 0;
  border-top: 1px solid transparent;

  @media (max-width: 1024px) {
    justify-content: flex-start;
    flex-wrap: wrap;
    row-gap: 10px;
    border-top-color: #f0f0f0;
  }
  
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: auto 1fr;
    justify-content: stretch;
    gap: 10px 4px;
    padding: 12px 12px 14px;
  }
  
  .required-star {
    color: #ff4d4f;
    margin-right: 4px;
    font-size: 14px;
  }
  
  .label-text {
    font-size: 14px;
    margin-right: 16px;
    font-weight: 500;
    color: #191919;

    @media (max-width: 768px) {
      margin-right: 0;
    }
  }
  
  .code-input {
    width: 280px;
    margin-right: 24px;
    font-weight: 400;

    @media (max-width: 1024px) {
      flex: 1 1 260px;
      min-width: 220px;
    }
    
    @media (max-width: 768px) {
      grid-column: 1 / -1;
      width: 100%;
      min-width: 0;
      margin-right: 0;
    }
  }
  
  .join-btn {
    width: 120px;
    height: 36px;
    background-color: #0D62F3;
    border-color: #0D62F3;
    
    @media (max-width: 768px) {
      grid-column: 1 / -1;
      width: 100%;
      margin-left: 0;
    }
    
    &:hover {
      background-color: #3b7cf0;
      border-color: #3b7cf0;
    }
  }
}
</style>
